import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Card, Stack, IconButton, Tooltip, TextField, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { formatBRL } from '../../utils/formatBRL';

const REPORT_DAYS = 45;

const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
const API_KEY = import.meta.env.VITE_API_SECRET_KEY;
const headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

interface ProductSale { sku?: string; title?: string; units: number; gross: number; proceeds: number; currency?: string; }
interface Report {
  grossTotal: number; proceedsTotal: number; unitsTotal: number;
  byProduct: ProductSale[]; daysScanned: number; daysWithData: string[];
}

export const AdminAppleSales: React.FC = () => {
  const { theme } = useTheme();
  const [vendorNumber, setVendorNumber] = useState<string | null>(null);
  const [vendorInput, setVendorInput] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const surface = { p: 2.5, backgroundColor: theme.colors.background, borderRadius: theme.radii.large, boxShadow: theme.shadows.small };

  const loadConfigAndReport = async () => {
    setLoading(true); setError(null);
    try {
      const cfg = await fetch(`${API_URL}/admin/appstore/config`, { headers }).then(r => r.json());
      if (!cfg.vendorNumber) { setVendorNumber(null); setLoading(false); return; }
      setVendorNumber(cfg.vendorNumber);
      await loadReport();
    } catch (e: any) {
      setError(e.message); setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/sales-report?days=${REPORT_DAYS}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar relatório');
      setReport(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveVendor = async () => {
    if (!vendorInput.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/config`, {
        method: 'POST', headers, body: JSON.stringify({ vendorNumber: vendorInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setVendorNumber(vendorInput.trim());
      await loadReport();
    } catch (e: any) {
      setError(e.message); setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { loadConfigAndReport(); }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Box>
          <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 800, letterSpacing: -0.5 }}>
            Vendas Apple
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 0.5 }}>
            Receita real da App Store (últimos {REPORT_DAYS} dias). Vendas via Stripe estão na aba Visão Geral.
          </Typography>
        </Box>
        <Tooltip title="Recarregar">
          <IconButton onClick={loadReport} size="small" aria-label="Recarregar vendas da Apple" sx={{ border: `1px solid ${theme.colors.border}` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2, borderRadius: theme.radii.medium }}>{error}</Alert>}

      {/* Sem vendor number: pedir uma vez */}
      {!loading && !vendorNumber && (
        <Card sx={{ ...surface, mt: 2 }}>
          <Typography variant="body2" sx={{ color: theme.colors.text.primary, mb: 1.5 }}>
            Pra puxar as vendas reais, informe o <b>Vendor Number</b> (App Store Connect → Pagamentos e Relatórios Financeiros, número de 8 dígitos no topo).
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label="Vendor Number" value={vendorInput}
              onChange={e => setVendorInput(e.target.value.replace(/\D/g, ''))}
              size="small" sx={{ maxWidth: 220 }}
            />
            <Button variant="contained" onClick={saveVendor} disabled={saving || !vendorInput.trim()} sx={{ backgroundColor: theme.colors.primary }}>
              {saving ? <CircularProgress aria-label="Carregando" size={20} sx={{ color: '#fff' }} /> : 'Salvar e puxar vendas'}
            </Button>
          </Stack>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Carregando" /></Box>
      ) : vendorNumber && report ? (
        <>
          <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Card sx={{ ...surface, minWidth: 180 }}>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Receita bruta</Typography>
              <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 800 }}>R$ {formatBRL(report.grossTotal)}</Typography>
            </Card>
            <Card sx={{ ...surface, minWidth: 180 }}>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Você recebe (líquido)</Typography>
              <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 800 }}>R$ {formatBRL(report.proceedsTotal)}</Typography>
            </Card>
            <Card sx={{ ...surface, minWidth: 140 }}>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Unidades</Typography>
              <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 800 }}>{report.unitsTotal}</Typography>
            </Card>
          </Stack>

          {report.byProduct.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: theme.radii.medium }}>
              Nenhuma venda nos últimos {report.daysScanned} dias. (Relatórios da Apple têm ~1-2 dias de atraso.)
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {report.byProduct.map((p, i) => (
                <Card key={i} sx={{ ...surface, py: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>{p.title || p.sku}</Typography>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>{p.sku} · {p.units} unidade(s)</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: theme.colors.text.primary, fontWeight: 800 }}>R$ {formatBRL(p.gross)}</Typography>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>líquido R$ {formatBRL(p.proceeds)}</Typography>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mt: 2 }}>
            Vendor {vendorNumber} · {report.daysWithData.length} dia(s) com dados nos últimos {report.daysScanned}.
          </Typography>
        </>
      ) : null}
    </Box>
  );
};
